import { createFileRoute } from "@tanstack/react-router";
import { HubShell, Section, InfoGrid, hubHead } from "@/components/work-hubs/HubShell";

export const Route = createFileRoute("/app-testing/guidelines")({
  head: () =>
    hubHead(
      "/app-testing/guidelines",
      "App Tester Guidelines — Sample Test, Mic Setup & Common Failures | DrHint",
      "Walk through a sample usability test, set up your microphone properly, and avoid the most common reasons testers get rejected.",
    ),
  component: Page,
});

function Page() {
  return (
    <HubShell
      crumbs={[{ label: "Home", to: "/" }, { label: "App Testing", to: "/app-testing" }, { label: "Guidelines" }]}
      step={2}
      eyebrow="Testing Hub"
      title="Tester Benchmark Guidelines"
      intro="Platforms rate every test you submit. Good ratings unlock more tests; poor ones can get your account limited. These are the habits that matter most."
      back={{ to: "/app-testing", label: "Back to calculator" }}
      next={{ to: "/app-testing/portals", label: "Access Official Tester Portals" }}
    >
      <Section title="Sample test walkthrough">
        <ol className="list-decimal space-y-2 pl-5">
          <li><b>Read the scenario aloud.</b> e.g. "You want to book a hotel in Dubai for two nights."</li>
          <li><b>Share first impressions</b> of the home screen before clicking anything.</li>
          <li><b>Think aloud constantly</b> — say what you are looking for, what confuses you and why.</li>
          <li><b>Do not skip tasks.</b> If you get stuck, explain where and keep trying for a minute.</li>
          <li><b>Answer written questions</b> in full sentences with specific examples.</li>
        </ol>
      </Section>

      <Section title="Microphone & recording setup">
        <InfoGrid items={[
          { title: "Use a headset mic", body: "Even cheap wired earphones with a mic sound clearer than a laptop's built-in microphone." },
          { title: "Quiet room", body: "Turn off fans and TV where possible. Background noise is a top rejection reason." },
          { title: "Run the practice test", body: "Most platforms offer one. Listen back to make sure your voice is clear and not too quiet." },
          { title: "Close private tabs", body: "Your screen is recorded. Close email, chats and banking tabs before starting." },
        ]} />
      </Section>

      <Section title="Common failure reasons">
        <ul className="list-disc space-y-2 pl-5">
          <li>Long silences — the client cannot learn anything without your spoken thoughts.</li>
          <li>Rushing through tasks to finish faster.</li>
          <li>One-word answers like "good" or "easy".</li>
          <li>Answering screeners dishonestly to qualify — inconsistent answers are often detected.</li>
          <li>Poor audio or a frozen screen recording.</li>
        </ul>
      </Section>

      <Section title="Screening advice">
        <p>Screener questions decide whether you match the client's target user (for example, "Do you shop online weekly?"). Answer truthfully — fewer tests is better than a banned account. Complete your profile fully so platforms can match you to more studies, and check the dashboard several times a day because tests fill quickly.</p>
      </Section>
    </HubShell>
  );
}
